import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getMe } from '../../apis';
import useVoyagerStore from '../../stores/useVoyagerStore';
import {
  IMPACT_DIMENSIONS, bytes, groupLabel, latestWeek, money, readActivity, readOwnFeedback, readUsage, released, weekLabel,
  type ActivityItem, type FeedbackSummary, type UsageDimension, type UsageWeek,
} from './impactApi';

/**
 * Build › Impact (#1516, S16 of #1500). Usage (questions by channel, route
 * and outcome; people asking), Trust (refusals, cost per question, warehouse use, the caller's own ratings) and the
 * Activity timeline. Every number is read from S15's admin route (`/api/usage/weekly`) or an existing endpoint as
 * the caller may read it (see `impactApi.ts`); each element that shows one names its source in `data-source`.
 * What is not measured yet says so and is never estimated. A non-admin reads nothing here and sees no workspace
 * number: the server refuses them too (403), which the page treats the same way.
 */
export const NOT_ENOUGH = 'Not enough people asking yet';
export const NOT_MEASURED = 'Not measured yet';

type Read<T> = { state: 'loading' } | { state: 'ok'; value: T } | { state: 'error' } | { state: 'denied' };
type Usage = Record<UsageDimension, Read<UsageWeek[]>>;

const statusOf = (e: any): number | undefined => e?.response?.status;

export default function ImpactPage() {
  // a workspace switch reads everything again, for the new workspace only
  const generation = useVoyagerStore((s) => s.threadsGeneration);
  return <Impact key={String(generation)} />;
}

function Impact() {
  const [admin, setAdmin] = useState<boolean | null>(null);
  const [usage, setUsage] = useState<Usage>({ channel: { state: 'loading' }, route: { state: 'loading' }, outcome: { state: 'loading' } });
  const [feedback, setFeedback] = useState<Read<FeedbackSummary | null>>({ state: 'loading' });
  const [activity, setActivity] = useState<Read<ActivityItem[]>>({ state: 'loading' });

  useEffect(() => {
    let live = true;
    getMe().then((me) => {
      if (!live) return;
      const isAdmin = me?.can_set_policy === true;
      setAdmin(isAdmin);
      if (!isAdmin) return;
      for (const dimension of IMPACT_DIMENSIONS) {
        readUsage(dimension).then(
          (value) => { if (live) setUsage((u) => ({ ...u, [dimension]: { state: 'ok', value } })); },
          (e) => {
            if (!live) return;
            if (statusOf(e) === 403) setAdmin(false);
            setUsage((u) => ({ ...u, [dimension]: { state: 'error' } }));
          },
        );
      }
      readOwnFeedback().then((value) => { if (live) setFeedback({ state: 'ok', value }); }, () => { if (live) setFeedback({ state: 'error' }); });
      readActivity().then((value) => { if (live) setActivity({ state: 'ok', value }); }, () => { if (live) setActivity({ state: 'error' }); });
    }, () => { if (live) setAdmin(false); });
    return () => { live = false; };
  }, []);

  return <Box data-testid="simple-page-impact" sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'var(--bg)' }}>
    <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, md: 4 }, pt: 4, pb: 6, display: 'flex', flexDirection: 'column', gap: 2.75 }}>
      <Box>
        <Typography component="h1" sx={{ fontSize: 24, fontWeight: 600, letterSpacing: '-.01em', mb: 0.5 }}>Impact</Typography>
        <Typography sx={{ color: 'var(--ink-3)', fontSize: 13.5, maxWidth: '70ch' }}>
          How Stroma is doing for your workspace: how much people rely on it and how it holds up. Every number here is measured by Stroma, never estimated.
        </Typography>
      </Box>
      {admin === null && <Typography role="status" sx={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</Typography>}
      {admin === false && <Typography data-testid="impact-admins-only" sx={{ color: 'var(--ink-2)', fontSize: 13.5, border: '1px solid var(--line)', borderRadius: '12px', px: 2, py: 1.5 }}>
        Workspace usage is shown to workspace admins only.
      </Typography>}
      {admin === true && <>
        <UsageSection usage={usage} />
        <TrustSection usage={usage} feedback={feedback} />
        <ActivitySection activity={activity} />
        <Typography sx={{ fontSize: 12, color: 'var(--ink-3)' }}>
          Counted from completed weeks (Monday to Sunday) since usage counting was turned on. A week, or a breakdown of it, is shown only when every group in it had at least three people asking.
        </Typography>
      </>}
    </Box>
  </Box>;
}

/** The totals: one read's weeks (each read carries the same closed-week totals), the first that loaded. */
function totals(usage: Usage): Read<UsageWeek[]> {
  const loaded = IMPACT_DIMENSIONS.map((d) => usage[d]).find((r) => r.state === 'ok');
  if (loaded) return loaded;
  return IMPACT_DIMENSIONS.some((d) => usage[d].state === 'loading') ? { state: 'loading' } : { state: 'error' };
}

function Head({ title, line, week }: { title: string; line?: string; week?: boolean }) {
  return <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
    <Typography component="h2" sx={{ fontWeight: 600, fontSize: 15 }}>{title}</Typography>
    {line && <Typography component="span" data-source={week ? 'usage.week' : undefined} sx={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{line}</Typography>}
  </Box>;
}

const kpisSx = {
  display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, border: '1px solid var(--line)', borderRadius: '14px', overflow: 'hidden',
  '& > *': { px: 2.25, py: 1.75, borderRight: '1px solid var(--line-2)', borderBottom: { xs: '1px solid var(--line-2)', md: 0 } },
} as const;

function Tile({ id, source, label, value, sub }: { id: string; source: string; label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  const quiet = value === NOT_ENOUGH || value === NOT_MEASURED;
  return <Box data-testid={`impact-tile-${id}`} data-source={source} sx={{ minWidth: 0 }}>
    <Typography component="span" sx={{ fontSize: 12, color: 'var(--ink-3)', display: 'block' }}>{label}</Typography>
    <Typography component="b" data-testid={`impact-value-${id}`} sx={quiet
      ? { display: 'block', fontSize: 13.5, color: 'var(--ink-2)', py: 0.75 }
      : { display: 'block', fontSize: 24, fontWeight: 500, lineHeight: 1.3, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {sub && <Typography component="small" sx={{ fontSize: 12, color: 'var(--ink-3)', display: 'block' }}>{sub}</Typography>}
  </Box>;
}

const loadingOr = (read: Read<unknown>, failed = 'Could not load') => (read.state === 'loading' ? 'Loading…' : read.state === 'error' ? failed : null);

function UsageSection({ usage }: { usage: Usage }) {
  const all = totals(usage);
  const week = all.state === 'ok' ? latestWeek(all.value) : null;
  const total = week && !week.withheld ? week.total : null;
  const pending = loadingOr(all);
  const headline = pending ?? (!week ? 'Nothing counted yet' : total ? null : NOT_ENOUGH);
  const channel = usage.channel.state === 'ok' ? usage.channel.value.find((w) => week && w.week === week.week) ?? null : null;
  const channels = released(channel);
  return <Box component="section" data-testid="impact-usage" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Head title="Usage" week={!!week} line={week ? `Week of ${weekLabel(week.week)}` : 'Completed weeks'} />
    <Box sx={{ ...kpisSx, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' } }}>
      <Tile id="questions" source="usage.total.asks" label="Questions asked" value={headline ?? total!.asks.toLocaleString('en-US')} />
      <Tile id="people" source="usage.total.askers" label="People asking" value={headline ?? total!.askers.toLocaleString('en-US')} />
      <Tile id="channels" source="usage.channel" label="By channel" value={headline ?? (channels
        ? <Box component="span" sx={{ fontSize: 13.5, fontWeight: 400, letterSpacing: 0, display: 'block', py: 0.75 }}>
          {channels.map((g) => `${groupLabel('channel', g.value)} ${g.asks.toLocaleString('en-US')}`).join(' · ')}</Box>
        : loadingOr(usage.channel) ?? NOT_ENOUGH)} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Breakdown dimension="route" title="By route" read={usage.route} week={week} />
      <Breakdown dimension="outcome" title="By outcome" read={usage.outcome} week={week} />
    </Box>
    <Weeks read={all} />
  </Box>;
}

const boxSx = { border: '1px solid var(--line)', borderRadius: '14px', overflow: 'hidden', overflowX: 'auto', bgcolor: 'var(--bg)' } as const;
const thSx = { textAlign: 'left', fontWeight: 500, color: 'var(--ink-3)', fontSize: 12, px: 1.75, py: 1.1, borderBottom: '1px solid var(--line)', bgcolor: 'var(--sunk)', whiteSpace: 'nowrap' } as const;
const tdSx = { px: 1.75, py: 1.25, borderBottom: '1px solid var(--line-2)', fontSize: 13 } as const;
const num = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

function Breakdown({ dimension, title, read, week }: { dimension: UsageDimension; title: string; read: Read<UsageWeek[]>; week: UsageWeek | null }) {
  const own = read.state === 'ok' && week ? read.value.find((w) => w.week === week.week) ?? null : null;
  const groups = released(own);
  const note = loadingOr(read) ?? (!week ? 'Nothing counted yet' : groups ? (groups.length ? null : 'Nothing counted yet') : NOT_ENOUGH);
  return <Box data-testid={`impact-breakdown-${dimension}`} data-source={`usage.${dimension}`} sx={boxSx}>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr>
        <Box component="th" sx={thSx}>{title}</Box>
        <Box component="th" sx={{ ...thSx, ...num }}>People</Box>
        <Box component="th" sx={{ ...thSx, ...num }}>Questions</Box>
      </tr></thead>
      <tbody>
        {note ? <tr><Box component="td" colSpan={3} sx={{ ...tdSx, color: 'var(--ink-3)', borderBottom: 0 }}>{note}</Box></tr>
          : groups!.map((g) => <tr key={g.value}>
            <Box component="td" sx={tdSx}>{groupLabel(dimension, g.value)}</Box>
            <Box component="td" sx={{ ...tdSx, ...num }}>{g.askers.toLocaleString('en-US')}</Box>
            <Box component="td" sx={{ ...tdSx, ...num }}>{g.asks.toLocaleString('en-US')}</Box>
          </tr>)}
      </tbody>
    </Box>
  </Box>;
}

function Weeks({ read }: { read: Read<UsageWeek[]> }) {
  if (read.state !== 'ok' || read.value.length < 2) return null;
  const weeks = [...read.value].sort((a, b) => (a.week < b.week ? 1 : -1));
  return <Box data-testid="impact-weeks" data-source="usage.total" sx={boxSx}>
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr>
        <Box component="th" sx={thSx}>Week</Box>
        <Box component="th" sx={{ ...thSx, ...num }}>People</Box>
        <Box component="th" sx={{ ...thSx, ...num }}>Questions</Box>
        <Box component="th" sx={{ ...thSx, ...num }}>Model cost</Box>
      </tr></thead>
      <tbody>{weeks.map((w) => <tr key={w.week} data-week={w.week}>
        <Box component="td" sx={tdSx}>{weekLabel(w.week)}</Box>
        {w.withheld || !w.total
          ? <Box component="td" colSpan={3} sx={{ ...tdSx, ...num, color: 'var(--ink-3)' }}>{NOT_ENOUGH}</Box>
          : <>
            <Box component="td" sx={{ ...tdSx, ...num }}>{w.total.askers.toLocaleString('en-US')}</Box>
            <Box component="td" sx={{ ...tdSx, ...num }}>{w.total.asks.toLocaleString('en-US')}</Box>
            <Box component="td" sx={{ ...tdSx, ...num }}>{money(w.total.cost_usd)}</Box>
          </>}
      </tr>)}</tbody>
    </Box>
  </Box>;
}

function TrustSection({ usage, feedback }: { usage: Usage; feedback: Read<FeedbackSummary | null> }) {
  const all = totals(usage);
  const week = all.state === 'ok' ? latestWeek(all.value) : null;
  const total = week && !week.withheld ? week.total : null;
  const pending = loadingOr(all);
  const none = pending ?? (!week ? 'Nothing counted yet' : total ? null : NOT_ENOUGH);
  const outcomeWeek = usage.outcome.state === 'ok' && week ? usage.outcome.value.find((w) => w.week === week.week) ?? null : null;
  const outcomes = released(outcomeWeek);
  const count = (value: string) => outcomes?.find((g) => g.value === value)?.asks ?? 0;
  const asked = outcomes ? count('asked_back') : 0;
  const ratings = feedback.state === 'ok' ? feedback.value : null;
  return <Box component="section" data-testid="impact-trust" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Head title="Trust" week={!!week} line={week ? `Why these answers can be relied on · week of ${weekLabel(week.week)}` : 'Why these answers can be relied on'} />
    <Box sx={{ ...kpisSx, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' } }}>
      <Tile id="tests" source="not-measured" label="Correct on test questions" value={NOT_MEASURED} />
      <Tile id="refused" source="usage.outcome.refused" label="Refused instead of guessing" value={none ?? (outcomes
        ? count('refused').toLocaleString('en-US') : loadingOr(usage.outcome) ?? NOT_ENOUGH)}
        sub={!none && outcomes && asked ? `and ${asked.toLocaleString('en-US')} asked back first` : undefined} />
      <Tile id="cost" source="usage.total.cost_usd/asks" label="Cost per question" value={none ?? money(total!.cost_usd / total!.asks)}
        sub={!none ? 'average model cost; routing not yet counted' : undefined} />
      <Tile id="warehouse" source={total && total.warehouse_bytes !== null ? 'usage.total.warehouse_bytes' : 'not-measured'} label="Warehouse read"
        value={total ? (total.warehouse_bytes !== null ? bytes(total.warehouse_bytes) : NOT_MEASURED) : none ?? NOT_MEASURED} />
      <Tile id="ratings" source="feedback.last_7d" label="Your ratings" value={loadingOr(feedback) ?? (ratings
        ? `${ratings.last_7d.up.toLocaleString('en-US')} up · ${ratings.last_7d.down.toLocaleString('en-US')} down` : NOT_MEASURED)}
        sub="yours only, last 7 days" />
    </Box>
  </Box>;
}

function ActivitySection({ activity }: { activity: Read<ActivityItem[]> }) {
  const items = activity.state === 'ok' ? activity.value.slice(0, 20) : [];
  const note = loadingOr(activity) ?? (items.length ? null : 'Nothing has happened in the last 30 days.');
  const day = (at: string | null) => {
    const d = at ? new Date(at) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  };
  return <Box component="section" data-testid="impact-activity" data-source="activity" sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    <Head title="Activity" line="Notable moments in the last 30 days, newest first" />
    <Box sx={{ ...boxSx, px: 2, py: 1 }}>
      {note ? <Typography sx={{ fontSize: 13, color: 'var(--ink-3)', py: 0.75 }}>{note}</Typography>
        : items.map((r) => <Box key={r.id} sx={{ display: 'grid', gridTemplateColumns: '52px 16px 1fr', gap: 1.25, py: 0.875, fontSize: 12.5, alignItems: 'start' }}>
          <Box component="time" sx={{ fontFamily: 'var(--mono)', fontSize: 11.5, lineHeight: 1.7, color: 'var(--ink-3)' }}>{day(r.at)}</Box>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.event === 'FAILED' ? 'var(--run)' : 'var(--ok)', mt: '6px', justifySelf: 'center' }} />
          <Box component="span">{r.title}</Box>
        </Box>)}
    </Box>
  </Box>;
}
